import React from 'react'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  getRatingBucket,
  getSentimentColorGroup,
} from 'features/charts-v2/constants'
import { METRICS } from 'features/charts-v2/types'
import {
  calculateMaxTextWidth,
  calculateRotatedXAxisRightOverhang,
  truncateText,
  wrapText,
  X_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
  Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
} from 'features/charts-v2/utils/axis-tick-utils'

// Inject keyframe animation for tooltip fade-in and fade-out
const styleSheet = document.createElement('style')
styleSheet.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`
if (!document.head.querySelector('style[data-heatmap-tooltip]')) {
  styleSheet.setAttribute('data-heatmap-tooltip', 'true')
  document.head.appendChild(styleSheet)
}

/**
 * Custom Heatmap Component
 */
interface HeatmapProps {
  heatmapData: Array<{
    row: string
    column: string
    primaryMetricValue: number
    sentiment: number
    recordCount: number
    secondaryMetricValue: number
    pctVolumeRow: number
    pctVolumeColumn: number
    color: string
  }>
  rows: string[]
  columns: string[]
  yAxisLabel?: string
  xAxisLabel?: string
  onDataPointHover?: (dataPoint: string | null) => void
  hoveredDataPoint?: string | null
  onSegmentClick?: (
    segmentData: {
      primaryBreakdown: string
      stackingBreakdown?: string
      value: number
      payload: Record<string, unknown>
    },
    event: React.MouseEvent,
  ) => void
  fontSizePx?: number
  showXAxisLabel?: boolean
  showYAxisLabel?: boolean
  xAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  yAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  metricLabel?: string
  secondaryMetricLabel?: string
  primaryMetricId?: string
  secondaryMetricId?: string
  scaleVolume?: boolean
}

// Separator for the composite (row, column) hover key. U+2063 INVISIBLE
// SEPARATOR won't collide with any real row/column label, and — unlike the
// previous U+0000 — it is a valid XML 1.0 character. The key is written to the
// `data-heatmap-cell` DOM attribute, and a NULL byte there made the whole
// heatmap SVG invalid XML, so it failed to load as an image and got dropped
// from PPTX/PNG export while rendering fine on screen (PROD-3939).
const HOVER_KEY_SEPARATOR = '\u2063'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Dim non-hovered cells when any cell is hovered */
  &:has(rect[data-heatmap-cell]:hover) rect[data-heatmap-cell] {
    opacity: ${HOVER_DIMMED_OPACITY};
    transition: ${HOVER_TRANSITION_CSS};
  }

  /* Highlight the hovered cell */
  &:has(rect[data-heatmap-cell]:hover) rect[data-heatmap-cell]:hover {
    opacity: ${HOVER_FOCUSED_OPACITY} !important;
    stroke: #1e40af !important;
    stroke-width: 2 !important;
  }
`

export const CustomHeatmap: React.FC<HeatmapProps> = ({
  heatmapData,
  rows,
  columns,
  yAxisLabel = 'Row',
  xAxisLabel = 'Column',
  onDataPointHover,
  hoveredDataPoint,
  onSegmentClick,
  fontSizePx = 13,
  showXAxisLabel = true,
  showYAxisLabel = true,
  xAxisOverflow = 'expand',
  yAxisOverflow = 'expand',
  metricLabel = 'Sentiment',
  secondaryMetricLabel = 'Sentences',
  primaryMetricId,
  secondaryMetricId,
  scaleVolume = false,
}) => {
  const [dimensions, setDimensions] = React.useState({
    width: 800,
    height: 600,
  })
  const [mousePosition, setMousePosition] = React.useState<{
    x: number
    y: number
  } | null>(null)
  const [internalHoveredCell, setInternalHoveredCell] = React.useState<
    string | null
  >(null)
  const [visibleTooltip, setVisibleTooltip] = React.useState<string | null>(
    null,
  )
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)

  // Merge local hover state with prop for external-driven hover (e.g., table row hover)
  const effectiveHover = internalHoveredCell ?? hoveredDataPoint

  React.useEffect(() => {
    const updateDimensions = (): void => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setDimensions({ width: rect.width, height: rect.height })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const minCellWidth = 30
  const minCellHeight = 20

  // Calculate dynamic margins based on overflow mode and label content
  let marginTop: number
  let marginRight = 20 // default for wrap/truncate/hidden
  let marginLeft: number

  if (showXAxisLabel) {
    if (xAxisOverflow === 'expand') {
      // Calculate based on longest label when rotated at -45 degrees
      // Remove max constraint to allow full label display
      const font = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      const maxWidth = calculateMaxTextWidth(columns, font)
      // ~5% headroom on the rotated-label projection: canvas measureText slightly
      // under-measures the rendered SVG font and the label's own thickness adds a
      // small projection onto each axis.
      const LABEL_PROJECTION_SAFETY = 1.05
      // For labels rotated at -45 degrees: height = textWidth * sin(45deg) + buffer
      // sin(45deg) ~ 0.707, add extra buffer for proper spacing
      marginTop = Math.max(80, maxWidth * 0.707 * LABEL_PROJECTION_SAFETY + 40)
      // Compute only the overhang of columns near the right edge rather than using
      // the global max label width, which over-reserves when the longest label sits
      // in the middle of the grid. minCellWidth gives a safe upper bound (real
      // cellWidth >= min => real overhang <= this estimate).
      const rightOverhang = calculateRotatedXAxisRightOverhang(
        columns,
        fontSizePx,
        minCellWidth,
        LABEL_PROJECTION_SAFETY,
      )
      marginRight = Math.max(20, rightOverhang + 8)
    } else if (xAxisOverflow === 'wrap') {
      marginTop = 120
    } else {
      marginTop = 80
    }
  } else {
    marginTop = 20
  }

  if (showYAxisLabel) {
    if (yAxisOverflow === 'expand') {
      // Calculate based on longest label text width without max constraint
      const font = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      // Add generous buffer (30px) to ensure no cutoff
      marginLeft = Math.max(100, calculateMaxTextWidth(rows, font) + 30)
    } else if (yAxisOverflow === 'wrap') {
      marginLeft = 180
    } else {
      marginLeft = 200
    }
  } else {
    marginLeft = 20
  }

  const margin = {
    top: marginTop,
    right: marginRight,
    bottom: 40,
    left: marginLeft,
  }

  const containerWidth = dimensions.width
  const containerHeight = dimensions.height

  const requiredWidth =
    margin.left + margin.right + columns.length * minCellWidth
  const requiredHeight =
    margin.top + margin.bottom + rows.length * minCellHeight

  const svgWidth = Math.max(containerWidth, requiredWidth)
  const svgHeight = Math.max(containerHeight, requiredHeight)
  const gridWidth = svgWidth - margin.left - margin.right
  const gridHeight = svgHeight - margin.top - margin.bottom

  // Calculate cell dimensions to fill the available space with no gaps
  const cellWidth = Math.max(minCellWidth, gridWidth / columns.length)
  const cellHeight = Math.max(minCellHeight, gridHeight / rows.length)
  const cellGap = 5

  // Helper to create hover key that won't collide with row/column names
  const createHoverKey = (row: string, column: string): string =>
    `${row}${HOVER_KEY_SEPARATOR}${column}`

  // Helper to parse hover key
  const parseHoverKey = (
    key: string,
  ): { row: string; column: string } | null => {
    const parts = key.split(HOVER_KEY_SEPARATOR)
    if (parts.length !== 2) return null
    return { row: parts[0]!, column: parts[1]! }
  }

  // Pre-compute lookup map for O(1) data access instead of O(n) find() calls
  const dataMap = React.useMemo(
    () => new Map(heatmapData.map((d) => [createHoverKey(d.row, d.column), d])),
    [heatmapData],
  )

  // Per-row max secondary metric; cells are scaled relative to the
  // heaviest cell in their row when Scale Volume is on.
  const maxSecondaryByRow = React.useMemo(() => {
    const map = new Map<string, number>()
    heatmapData.forEach((d) => {
      const prev = map.get(d.row) ?? 0
      if (d.secondaryMetricValue > prev) {
        map.set(d.row, d.secondaryMetricValue)
      }
    })
    return map
  }, [heatmapData])

  // Compute totals for percentage calculations
  const totalPrimaryMetric = React.useMemo(
    () => heatmapData.reduce((sum, d) => sum + d.primaryMetricValue, 0),
    [heatmapData],
  )
  const totalSecondaryMetric = React.useMemo(
    () => heatmapData.reduce((sum, d) => sum + d.secondaryMetricValue, 0),
    [heatmapData],
  )

  const handleMouseMove = (
    event: React.MouseEvent<SVGRectElement | HTMLDivElement>,
  ): void => {
    setMousePosition({
      x: event.clientX,
      y: event.clientY,
    })
  }

  const handleMouseLeave = (): void => {
    setMousePosition(null)
    setInternalHoveredCell(null)
    onDataPointHover?.(null)

    // Keep tooltip visible for fade-out animation
    hideTimeoutRef.current = setTimeout(() => {
      setVisibleTooltip(null)
    }, 200) // Match fade-out duration
  }

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  return (
    <ChartContainer>
      <div
        ref={containerRef}
        data-chart-export-root="true"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          position: 'relative',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg width={svgWidth} height={svgHeight}>
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            {/* Column labels (X-axis) */}
            {showXAxisLabel &&
              columns.map((column, columnIndex) => {
                let displayText: string | string[] = column

                // Apply overflow logic
                if (xAxisOverflow === 'truncate') {
                  displayText = truncateText(
                    column,
                    X_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
                  )
                } else if (xAxisOverflow === 'wrap') {
                  displayText = wrapText(column, 100, fontSizePx)
                }

                const xPos = columnIndex * cellWidth + cellWidth / 2
                // Move labels further from grid edge to prevent cutoff
                const yPos = -20

                // Render single line (expand or truncate modes)
                if (typeof displayText === 'string') {
                  return (
                    <text
                      key={`column-${column}`}
                      x={xPos}
                      y={yPos}
                      textAnchor="start"
                      fontSize={fontSizePx}
                      fill="#333"
                      transform={`rotate(-45, ${xPos}, ${yPos})`}
                    >
                      {displayText}
                    </text>
                  )
                }

                // Render multiple lines (wrap mode)
                return (
                  <text
                    key={`column-${column}`}
                    x={xPos}
                    y={yPos}
                    textAnchor="start"
                    fontSize={fontSizePx}
                    fill="#333"
                    transform={`rotate(-45, ${xPos}, ${yPos})`}
                  >
                    {displayText.map((line, index) => (
                      <tspan
                        key={index}
                        x={xPos}
                        dy={index === 0 ? 0 : fontSizePx + 2}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                )
              })}

            {/* Row labels (Y-axis) */}
            {showYAxisLabel &&
              rows.map((row, rowIndex) => {
                let displayText: string | string[] = row

                // Apply overflow logic
                if (yAxisOverflow === 'truncate') {
                  displayText = truncateText(
                    row,
                    Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
                  )
                } else if (yAxisOverflow === 'wrap') {
                  displayText = wrapText(row, 150, fontSizePx)
                }

                // Add more spacing from grid edge
                const xPos = -15
                const yPos = rowIndex * cellHeight + cellHeight / 2

                // Render single line (expand or truncate modes)
                if (typeof displayText === 'string') {
                  return (
                    <text
                      key={`row-${row}`}
                      x={xPos}
                      y={yPos}
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize={fontSizePx}
                      fill="#333"
                    >
                      {displayText}
                    </text>
                  )
                }

                // Render multiple lines (wrap mode)
                return (
                  <text
                    key={`row-${row}`}
                    x={xPos}
                    y={yPos}
                    textAnchor="end"
                    fontSize={fontSizePx}
                    fill="#333"
                  >
                    {displayText.map((line, index) => (
                      <tspan
                        key={index}
                        x={xPos}
                        dy={index === 0 ? 0 : fontSizePx + 2}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                )
              })}

            {/* Axis border lines */}
            <line
              x1={0}
              y1={0}
              x2={gridWidth}
              y2={0}
              stroke="#ccc"
              strokeWidth={1}
            />
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={gridHeight}
              stroke="#ccc"
              strokeWidth={1}
            />

            {/* Heatmap cells */}
            {rows.map((row, rowIndex) =>
              columns.map((column, columnIndex) => {
                const dataPoint = dataMap.get(createHoverKey(row, column))

                if (!dataPoint) return null

                const x = columnIndex * cellWidth + cellGap / 2
                const baseY = rowIndex * cellHeight + cellGap / 2
                const baseHeight = cellHeight - cellGap

                // When scaling by volume, each cell's height is the row's
                // band height × (this cell's secondary metric / max in row),
                // centered vertically within the row band so bars grow and
                // shrink symmetrically around the midline.
                let renderedHeight = baseHeight
                if (scaleVolume) {
                  const maxInRow = maxSecondaryByRow.get(row) ?? 0
                  const ratio =
                    maxInRow > 0 ? dataPoint.secondaryMetricValue / maxInRow : 0
                  renderedHeight = Math.max(2, baseHeight * ratio)
                }
                const y = scaleVolume
                  ? baseY + (baseHeight - renderedHeight) / 2
                  : baseY

                const cellKey = createHoverKey(row, column)

                // Legend-hover highlighting (MetricColorLegend): matches the
                // bucket id format RoundedHorizontalBar emits, so the shared
                // useChartHoverDimming hook can dim/focus heatmap cells the
                // same way it does horizontal bar segments.
                const metricBucket =
                  primaryMetricId === METRICS.SENTIMENT.id
                    ? getSentimentColorGroup(dataPoint.primaryMetricValue)
                    : primaryMetricId === METRICS.RATING.id
                      ? String(getRatingBucket(dataPoint.primaryMetricValue))
                      : undefined

                return (
                  <rect
                    key={cellKey}
                    x={x}
                    y={y}
                    width={cellWidth - cellGap}
                    height={renderedHeight}
                    data-heatmap-cell={cellKey}
                    data-metric-bucket={metricBucket}
                    fill={dataPoint.color}
                    stroke="#fff"
                    strokeWidth={0.5}
                    opacity={0.9}
                    style={{ cursor: 'pointer' }}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => {
                      // Clear any pending hide timeout
                      if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current)
                        hideTimeoutRef.current = null
                      }
                      setInternalHoveredCell(cellKey)
                      setVisibleTooltip(cellKey)
                      onDataPointHover?.(cellKey)
                    }}
                    onMouseLeave={() => {
                      setInternalHoveredCell(null)
                      onDataPointHover?.(null)
                      // Keep tooltip visible for fade-out animation
                      hideTimeoutRef.current = setTimeout(() => {
                        setVisibleTooltip(null)
                      }, 200) // Match fade-out duration
                    }}
                    onClick={(event: React.MouseEvent) => {
                      if (onSegmentClick) {
                        onSegmentClick(
                          {
                            primaryBreakdown: row,
                            stackingBreakdown: column,
                            value: dataPoint.recordCount,
                            payload: dataPoint,
                          },
                          event,
                        )
                      }
                    }}
                  />
                )
              }),
            )}
          </g>
        </svg>

        {/* Mouse-tracking HTML Tooltip */}
        {visibleTooltip &&
          mousePosition &&
          (() => {
            const parsed = parseHoverKey(visibleTooltip)
            if (!parsed) return null

            const dataPoint = dataMap.get(
              createHoverKey(parsed.row, parsed.column),
            )

            if (!dataPoint) return null

            const offset = 15
            const ttWidth = tooltipRef.current?.offsetWidth ?? 280
            const ttHeight = tooltipRef.current?.offsetHeight ?? 160

            let tooltipX = mousePosition.x + offset
            let tooltipY = mousePosition.y + offset

            // Flip left if overflowing right edge
            if (tooltipX + ttWidth > window.innerWidth) {
              tooltipX = mousePosition.x - ttWidth - offset
            }

            // Flip up if overflowing bottom edge
            if (tooltipY + ttHeight > window.innerHeight) {
              tooltipY = mousePosition.y - ttHeight - offset
            }

            // Clamp to viewport edges
            tooltipX = Math.max(
              0,
              Math.min(tooltipX, window.innerWidth - ttWidth),
            )
            tooltipY = Math.max(
              0,
              Math.min(tooltipY, window.innerHeight - ttHeight),
            )

            // Check if we're fading out (hover ended but tooltip still visible)
            const isLeaving =
              !effectiveHover || effectiveHover !== visibleTooltip

            const heatmapRows = [
              {
                label: metricLabel,
                value: dataPoint.primaryMetricValue.toFixed(2),
                percentage:
                  (primaryMetricId === METRICS.RECORD_COUNT.id ||
                    primaryMetricId === METRICS.SENTENCE_COUNT.id) &&
                  totalPrimaryMetric > 0
                    ? `${((dataPoint.primaryMetricValue / totalPrimaryMetric) * 100).toFixed(2)}%`
                    : null,
                color: null,
              },
              {
                label: secondaryMetricLabel,
                value: dataPoint.secondaryMetricValue.toLocaleString(),
                percentage:
                  (secondaryMetricId === METRICS.RECORD_COUNT.id ||
                    secondaryMetricId === METRICS.SENTENCE_COUNT.id) &&
                  totalSecondaryMetric > 0
                    ? `${((dataPoint.secondaryMetricValue / totalSecondaryMetric) * 100).toFixed(2)}%`
                    : null,
                color: null,
              },
              {
                label: `% Volume of ${yAxisLabel}`,
                value: `${dataPoint.pctVolumeRow.toFixed(2)}%`,
                percentage: null,
                color: null,
              },
              {
                label: `% Volume of ${xAxisLabel}`,
                value: `${dataPoint.pctVolumeColumn.toFixed(2)}%`,
                percentage: null,
                color: null,
              },
            ]

            return (
              <div
                ref={tooltipRef}
                style={{
                  position: 'fixed',
                  left: `${tooltipX}px`,
                  top: `${tooltipY}px`,
                  pointerEvents: 'none',
                  zIndex: 1000,
                  opacity: 0,
                  animation: isLeaving
                    ? 'fadeOut 0.5s ease-out forwards'
                    : 'fadeIn 0.5s ease-out forwards',
                }}
              >
                <ChartDatapointHoverAnnotation
                  title={`${dataPoint.row} × ${dataPoint.column}`}
                  rows={heatmapRows}
                  footer={null}
                  focusedField={null}
                />
              </div>
            )
          })()}
      </div>
    </ChartContainer>
  )
}
